import React, { useState } from "react";
import { Share, PlusSquare, Smartphone, X, Check } from "lucide-react";
import iaimsLogo from "../../assets/iaims-logo.png";

interface IPhoneAddToHomeScreenModalProps {
  open: boolean;
  onClose: () => void;
}

export const checkIsStandalone = (): boolean => {
  if (typeof window === "undefined") return false;
  const isStandaloneNavigator = (window.navigator as any).standalone === true;
  const isStandaloneDisplayMode =
    window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  return isStandaloneNavigator || isStandaloneDisplayMode;
};

const IPhoneAddToHomeScreenModal: React.FC<IPhoneAddToHomeScreenModalProps> = ({
  open,
  onClose,
}) => {
  const [step, setStep] = useState<"confirm" | "guide">("confirm");

  // CRITICAL RULE: If app is installed / in standalone mode, DO NOT open the modal anyway!
  if (!open || checkIsStandalone()) {
    return null;
  }

  const handleConfirmYes = () => {
    setStep("guide");
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem("iaims_a2hs_prompted", "true");
    } catch (e) {}
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-fadeIn">
      <div className="relative w-full max-w-[360px] sm:max-w-sm bg-white rounded-3xl shadow-2xl p-6 flex flex-col items-center text-center border border-gray-100 overflow-hidden">
        {/* Close Button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          title="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {step === "confirm" ? (
          <>
            {/* App Icon */}
            <div className="w-20 h-20 rounded-2xl bg-white border-2 border-orange-100 p-2 shadow-lg flex items-center justify-center mb-4 shrink-0 ring-4 ring-orange-50">
              <img
                src={iaimsLogo}
                alt="IAIMS Logo"
                className="w-full h-full object-contain rounded-xl"
              />
            </div>

            {/* Title & Description */}
            <h2 className="text-lg sm:text-xl font-extrabold text-gray-900 mb-1">
              Add IAIMS to Home Screen
            </h2>
            <p className="text-xs text-gray-500 mb-6 max-w-[270px] leading-relaxed">
              Would you like to add this website as an icon on your iPhone home screen for instant 1-tap access?
            </p>

            {/* Action Buttons */}
            <div className="w-full space-y-2.5">
              <button
                onClick={handleConfirmYes}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-[#F97316] to-[#EA580C] hover:from-[#EA580C] hover:to-[#C2410C] text-white text-xs font-bold shadow-md shadow-orange-500/25 active:scale-[0.98] transition-all flex items-center justify-center space-x-2"
              >
                <Smartphone className="w-4 h-4" />
                <span>Yes, Add Home Icon</span>
              </button>

              <button
                onClick={handleDismiss}
                className="w-full py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-semibold active:scale-[0.98] transition-all"
              >
                Maybe Later
              </button>
            </div>
          </>
        ) : (
          <>
            {/* iOS Installation Instructions */}
            <div className="w-12 h-12 rounded-2xl bg-orange-100 flex items-center justify-center text-[#F97316] mb-3 shrink-0">
              <Share className="w-6 h-6" />
            </div>

            <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-1">
              How to Add IAIMS Icon
            </h2>
            <p className="text-[11px] text-gray-500 mb-4">
              Follow these simple steps in Safari to place the icon on your iPhone home screen:
            </p>

            {/* Steps list */}
            <div className="w-full text-left space-y-3 bg-gray-50 p-3.5 rounded-2xl border border-gray-100 text-xs text-gray-700 mb-5">
              <div className="flex items-start space-x-2.5">
                <span className="w-5 h-5 rounded-full bg-[#F97316] text-white font-bold flex items-center justify-center text-[11px] shrink-0 mt-0.5">
                  1
                </span>
                <p>
                  Tap the <strong className="text-gray-900">Share</strong> icon{" "}
                  <Share className="w-4 h-4 inline text-[#007AFF] -mt-1 mx-0.5" /> in your Safari bottom bar.
                </p>
              </div>

              <div className="flex items-start space-x-2.5">
                <span className="w-5 h-5 rounded-full bg-[#F97316] text-white font-bold flex items-center justify-center text-[11px] shrink-0 mt-0.5">
                  2
                </span>
                <p>
                  Scroll down and tap <strong className="text-gray-900">Add to Home Screen</strong>{" "}
                  <PlusSquare className="w-4 h-4 inline text-gray-800 -mt-1 mx-0.5" />.
                </p>
              </div>

              <div className="flex items-start space-x-2.5">
                <span className="w-5 h-5 rounded-full bg-[#F97316] text-white font-bold flex items-center justify-center text-[11px] shrink-0 mt-0.5">
                  3
                </span>
                <p>
                  Tap <strong className="text-gray-900">Add</strong> in the top right corner.
                </p>
              </div>
            </div>

            <button
              onClick={handleDismiss}
              className="w-full py-3 rounded-xl bg-[#F97316] hover:bg-[#EA580C] text-white text-xs font-bold shadow-md active:scale-[0.98] transition-all flex items-center justify-center space-x-1.5"
            >
              <Check className="w-4 h-4" />
              <span>Got it</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default IPhoneAddToHomeScreenModal;
